import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "@mui/material/Select";
import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from '@mui/material/ListItemText';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import MultiSelectBase from "../../components/searchBar/MultiSelectBase";
import CountrySelect from '../../components/searchBar/CountrySelect';
import DateRangeFilter from '../../components/searchBar/DateRangeFilter';
import {
  DEFAULT_FCS_CCDETAIL_STATE,
} from "../../constants/constants";
import * as types from '../../constants/actionTypes';
import AddCircle from '@mui/icons-material/AddCircle';
import UserLevelFilter from "../../components/searchBar/UserLevelFilter";
import { makeStyles } from "@mui/styles";
import theme from '../../constants/theme';


const useStyles = makeStyles(() => ({
  formControl: {
    margin: theme.spacing(1),
    display: "flex",
    fontStyle: "italic",
  },
  selectControl: {
    width: 250,
  },
  selectedFilters: {
    display: "flex",
    flexWrap: "wrap",
  },
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

const FcsCCDetailFilter = () => {
  const classes = useStyles();
  const {
          filters,
          levelId,
          showAll,
          userAndLevelName,
          countryList,
          selectedCountries,
          fcsCcDetailState,
          fcsCcDetailChannelList,
          fcsCcDetailStatusList,
          selectedFcsCCDetailChannelList,
          selectedFcsCCDetailStatus,
  } = useSelector((state) => state.report);
  const dispatch = useDispatch();
  const setFilters = (filters) => dispatch({ type: types.SET_FILTERS, filters });
  const selectCountries = (val) => dispatch({ type: types.SET_SELECTED_COUNTRIES, value: val });
  const setFcsCcDetailState = (val) => dispatch({ type: types.SET_FCS_CCDETAIL_STATE, value: val });
  const setSelectedContinents = (val) => dispatch({ type: types.SET_CONTINENTS_LIST, value: val });
  const setSelectedCCDetailChannels = (val) => dispatch({ type: types.SET_FCS_SELECTED_CCDETAIL_CHANNEL_LIST, value: val });
  const setSelectedCCDetailStatus = (val) => dispatch({ type: types.SET_FCS_SELECTED_CCDETAIL_STATUS, value: val });
  const setUserAndLevelName = (val) => dispatch({ type: types.SET_USER_AND_LEVEL_NAME, value: val });
  const setLevelId = (val) => dispatch({ type: types.SET_LEVEL_ID, value: val });
  const setShowAll = (val) => dispatch({ type: types.SET_SHOW_ALL, value: val });

  const filterList = [
    {
      label: "Date Range",
      value: "dateRange",
      keys: ["fromDate", "toDate"],
      required: true,
    },
    {
      label: "User & Level",
      value: "userAndLeve",
      keys: ["userAndLeve"],
      required: true,
    },
    {
      label: "Country",
      value: "country",
      keys: ["countryCodes"],
      show: true,
    },
    {
      label: "Credit Card Channels",
      value: "ccSelect",
      keys: ["ccSelect"],
      show: true,
    },
    {
      label: "Withdraw Status",
      value: "ccWithdrawStatus",
      keys: ["ccWithdrawStatus"],
      show: true,
    },
  ];

  const [selectedFilters, setSelectedFilters] = useState(
    filterList.filter((r) => r.required || r.show).map((r) => r.value)
  );

  const toggleFilter = (val) => {
    setSelectedFilters(val);
    const filterKeys = filterList
      .filter((r) => val.includes(r.value))
      .flatMap((r) => r.keys);
    const newFilters = { ...fcsCcDetailState};
    Object.keys(fcsCcDetailState).forEach((r) => {
      if (!(filterKeys.includes(r))) {
        newFilters[r] = DEFAULT_FCS_CCDETAIL_STATE[r];
      }
    });
    setFcsCcDetailState(newFilters)
    if (!filterKeys.includes('countryCodes')) {
      selectCountries([]);
      setSelectedContinents([]);
    }
  };

  const isVisible = (id) => {
    return selectedFilters.includes(id);
  };

  const selectedFilterCount = selectedFilters.length;

  useEffect(() => {
    const {
      fromDate,
      toDate,
      selectedFcsCCDetailStatus,
      selectedFcsCCDetailChannelList,
    } = fcsCcDetailState;

    const codes = selectedCountries.map(r => r.value).join(',');
    setFilters({
      fromDate,
      toDate,
      selectedFcsCCDetailStatus,
      selectedFcsCCDetailChannelList,
      countryCodes: codes || undefined,
      levelId,
      showAll
    });
  }, [fcsCcDetailState, selectedFcsCCDetailStatus, selectedFcsCCDetailChannelList, selectedCountries, levelId, showAll]);

  return (
    <>
      <div className={classes.formControl}>
        <InputLabel id="filter-mutiple-checkbox-label">
          <AddCircle color="primary" />
        </InputLabel>
        <Select
          className={classes.selectControl}
          labelId="filter-mutiple-checkbox-label"
          id="filter-mutiple-checkbox"
          multiple
          value={selectedFilters}
          onChange={(e) => toggleFilter(e.target.value)}
          input={<Input />}
          renderValue={(selected) => `${selectedFilterCount} filters selected`}
        >
          {filterList.map((option) => (
            <MenuItem
              key={option.value}
              disabled={option.required}
              value={option.value}
            >
              <Checkbox
                color="primary"
                checked={selectedFilters.includes(option.value)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <DateRangeFilter
        selectedFromDate={fcsCcDetailState.fromDate}
        setSelectedFromDate={(val) => setFcsCcDetailState({ ...fcsCcDetailState, fromDate: val })}
        selectedToDate={fcsCcDetailState.toDate}
        setSelectedToDate={(val) => setFcsCcDetailState({ ...fcsCcDetailState, toDate: val })}
      />

      {isVisible("userAndLeve") && (
          <UserLevelFilter
            levelId={levelId}
            setLevelId={setLevelId}
            showAll={showAll}
            setShowAll={setShowAll}
            userAndLevelName={userAndLevelName}
            setUserAndLevelName={setUserAndLevelName}
          />
        )}

      {isVisible("country") && (
        <CountrySelect
           data={countryList}
           onChange={(val) => selectCountries(val)}
           value={selectedCountries}
         />
      )}

      {isVisible("ccWithdrawStatus") && (
      <MultiSelectBase
          data={fcsCcDetailStatusList}
          onChange={(val) => {
            setFcsCcDetailState({ ...fcsCcDetailState, selectedFcsCCDetailStatus: val})
            setSelectedCCDetailStatus(val)
          }}
          value={fcsCcDetailState.selectedFcsCCDetailStatus}
          default_value="All Status"
          id="multiple-fcs-cc-status-select"
          label="Withdraw Status"
      />
      )}

      {isVisible("ccSelect") && (
      <MultiSelectBase
          data={fcsCcDetailChannelList}
          onChange={(val) => {
            setFcsCcDetailState({ ...fcsCcDetailState, selectedFcsCCDetailChannelList: val})
            setSelectedCCDetailChannels(val)
          }}
          value={fcsCcDetailState.selectedFcsCCDetailChannelList}
          default_value="All Channels"
          id="multiple-fcs-cc-channel-select"
          label="Credit Card Channels"
      />
      )}

    </>
  );
};

export default FcsCCDetailFilter;
